import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { MultimodalLiveClient } from '@/lib/multimodal-live-client';
import { GeminiAudioManager } from '@/lib/gemini-audio';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { captureCanvasViewport, captureScreenShare } from '@/lib/vision-capture';
import { createYouTubeContext, formatYouTubeContextMessage } from '@/lib/youtube-context';
import { devLogger } from '@/lib/dev-logger';
import { getResumptionToken, storeResumptionToken, clearResumptionToken } from '@/lib/sessionResumption';

export type AgentState = 'disconnected' | 'connecting' | 'authenticating' | 'connected' | 'ending' | 'error';

type SetMessages = (updater: (prev: any[]) => any[]) => void;

type SlideContextPayload = {
  slide: {
    id?: string;
    title?: string | null;
    slide_type?: string | null;
    section?: string | null;
    tutor_prompt?: string | null;
    duration_minutes?: number | null;
    slide_order?: number | null;
    slideOrder?: number | null;
    completed?: boolean;
  } | null;
  lessonTitle?: string;
  progress?: {
    total?: number;
    completed?: number;
    pendingTitles?: string[];
  };
};

type UseMultimodalLiveProps = {
  selectedDocument?: string | null;
  activeSessionId?: string | null;
  topicId?: string | null;
  setMessages?: SetMessages;
  setUnsavedMessageCount?: (updater: (prev: number) => number) => void;
  appendMessage?: (message: any) => void;
  messageSeqRef?: React.MutableRefObject<number>;
  onAgentActivityChange?: (isActive: boolean) => void;
  getCanvasEl?: (() => HTMLElement | null) | null;
  screenStream?: MediaStream | null;
  getYouTubePlayer?: (() => any) | null;
  currentYouTubeVideoId?: string | null;
  slideContext?: SlideContextPayload | null;
  onBlockAdded?: (data: { block_id: string; block_type: string; title?: string }) => void;
  onBlockUpdated?: (data: { block_id: string }) => void;
  onJournalUpdated?: (data: { journal_id: string; action: string }) => void;
  // Phase 8a: Getter for current messages to sync transcript to server
  getMessages?: () => any[];
  // Cost optimization: Journal ID for per-journal session resumption
  journalId?: string | null;
};

export function useMultimodalLive({
  selectedDocument,
  activeSessionId,
  topicId,
  setMessages,
  setUnsavedMessageCount,
  appendMessage,
  messageSeqRef,
  onAgentActivityChange,
  getCanvasEl,
  screenStream,
  getYouTubePlayer,
  currentYouTubeVideoId,
  slideContext,
  onBlockAdded,
  onBlockUpdated,
  onJournalUpdated,
  getMessages, // Phase 8a
  journalId, // Cost optimization
}: UseMultimodalLiveProps = {}) {
  const { user } = useAuth();
  const [agentStatus, setAgentStatus] = useState<AgentState>('disconnected');
  const [isAgentActive, setIsAgentActive] = useState(false);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [sessionDuration, setSessionDuration] = useState('00:00');
  const [isUserMuted, setIsUserMuted] = useState(true);
  const [isPushToTalkLocked, setIsPushToTalkLocked] = useState(false);
  const [isUserSpeakingVad, setIsUserSpeakingVad] = useState(false);

  // Cost optimization: Pre-flight ready state
  const [isPreFlightReady, setIsPreFlightReady] = useState(false);
  const [preFlightData, setPreFlightData] = useState<{
    authToken: string | null;
    userCredits: number;
    userProfile: any;
    journalContext: any;
  } | null>(null);

  // Cost optimization: Audio buffering during connection
  const audioBufferRef = useRef<ArrayBuffer[]>([]);
  const isBufferingRef = useRef<boolean>(false);
  const MAX_BUFFER_FRAMES = 500; // ~10 seconds at 50fps (20ms frames)

  // Cost optimization: Journal ID for session resumption
  const currentJournalIdRef = useRef<string | null>(journalId ?? null);

  // Infinite sessions: Auto-reconnection state
  const isAutoReconnectingRef = useRef<boolean>(false);
  const pendingReconnectRef = useRef<boolean>(false);
  const latestResumptionHandleRef = useRef<string | null>(null);
  const [isExtendingSession, setIsExtendingSession] = useState<boolean>(false);
  const handleConnectAgentRef = useRef<((mode?: string) => Promise<void>) | null>(null);

  const [client, setClient] = useState<MultimodalLiveClient | null>(null);
  const clientRef = useRef<MultimodalLiveClient | null>(null);
  const [audioManager, setAudioManager] = useState<GeminiAudioManager | null>(null);
  const sessionStartRef = useRef<number | null>(null);
  const hasEverConnectedRef = useRef<boolean>(false);
  const streamingMsgIdRef = useRef<string | null>(null);
  const streamingTextRef = useRef<string>("");
  const userStreamingMsgIdRef = useRef<string | null>(null);
  const userStreamingTextRef = useRef<string>("");
  const mutedRef = useRef<boolean>(true);
  useEffect(() => { mutedRef.current = isUserMuted; }, [isUserMuted]);
  const lastFrameSentRef = useRef<number>(0);
  const isCaptureInProgressRef = useRef<boolean>(false);
  const prevSpeakingRef = useRef<boolean>(false);
  const lastYTKeyRef = useRef<string | null>(null);
  const lastYTTimeRef = useRef<number>(0);
  const backendReadyRef = useRef<boolean>(false);
  const slideContextRef = useRef<SlideContextPayload | null>(slideContext ?? null);
  const lastSlideContextKeyRef = useRef<string | null>(null);
  const lastSlideNudgeKeyRef = useRef<string | null>(null);
  const lastLessonOverviewKeyRef = useRef<string | null>(null);

  const sendSlideContext = useCallback((context: SlideContextPayload | null, force: boolean = false) => {
    const targetClient = clientRef.current;
    if (!targetClient) return;
    if (!backendReadyRef.current) return;
    const progressKey = context?.progress
      ? `${context.progress.completed ?? 'NA'}/${context.progress.total ?? 'NA'}`
      : '';
    const key = context?.slide
      ? `${context.slide.id ?? 'unknown'}:${context.slide.tutor_prompt ?? ''}:${progressKey}`
      : `NO_SLIDE:${progressKey}`;
    if (!force && key === lastSlideContextKeyRef.current) {
      return;
    }
    const payload: Record<string, unknown> = {
      contextType: 'slide',
      lessonTitle: context?.lessonTitle,
      slide: context?.slide
        ? {
          id: context.slide.id,
          title: context.slide.title,
          slideType: context.slide.slide_type,
          section: context.slide.section,
          tutorPrompt: context.slide.tutor_prompt,
          durationMinutes: context.slide.duration_minutes,
        }
        : null,
      cleared: !context?.slide,
    };
    if (context?.progress) {
      payload.progress = {
        total: context.progress.total,
        completed: context.progress.completed,
        pendingTitles: context.progress.pendingTitles,
      };
    }
    try {
      targetClient.sendContextUpdate(payload);
      lastSlideContextKeyRef.current = key;
      devLogger.log('🧭 Sent slide context update', payload);
    } catch (error) {
      devLogger.warn('sendSlideContext failed', error);
    }
  }, []);

  const sendSlideKickoff = useCallback((context: SlideContextPayload | null) => {
    if (!context?.slide) {
      lastSlideNudgeKeyRef.current = null;
      return;
    }
    const targetClient = clientRef.current;
    if (!targetClient || !backendReadyRef.current) return;
    const key = context.slide.id || context.slide.title || JSON.stringify(context.slide);
    if (lastSlideNudgeKeyRef.current === key) return;

    const rawType =
      (context.slide as any).slide_type ||
      (context.slide as any).slideType ||
      "";
    const type = String(rawType).toLowerCase();
    const title = context.slide.title || context.slide.id || "this slide";
    const lessonLabel = context.lessonTitle
      ? ` in "${context.lessonTitle}"`
      : "";

    const total = context.progress?.total;
    const completed = context.progress?.completed ?? 0;
    const remainingTitles = context.progress?.pendingTitles ?? [];
    const slideOrder = context.slide.slide_order ?? context.slide.slideOrder ?? null;
    const slideIndexPhrase = total && slideOrder ? `Slide ${slideOrder} of ${total}` : slideOrder ? `Slide ${slideOrder}` : 'this slide';

    let prompt = `Slide ${slideIndexPhrase}: "${title}"${lessonLabel}.`;
    if (
      type.includes("assessment") ||
      type.includes("practice") ||
      type.includes("quiz") ||
      type.includes("faq") ||
      type.includes("question")
    ) {
      prompt += ` Let me try it first, then coach with hints if I stall and celebrate the key takeaway when I get it.`;
    } else if (type.includes("learn") || type.includes("intro")) {
      prompt += ` Offer either a quick walkthrough or stay on standby while I skim—ask which I prefer before diving in.`;
    }
    if (total && total > 0) {
      const remaining = Math.max(total - completed, 0);
      if (remaining > 0 && remainingTitles.length) {
        const preview = remainingTitles.slice(0, 2).join(' then ');
        prompt += ` After this we'll hit ${preview}.`;
      } else if (remaining === 0) {
        prompt += ` This finishes the lesson, so let's lock it in.`;
      }
    }

    try {
      targetClient.send([{ text: prompt }], true);
      lastSlideNudgeKeyRef.current = key;
      devLogger.log('🎯 Sent slide kickoff prompt', { prompt, slideId: context.slide.id });
    } catch (error) {
      devLogger.warn('sendSlideKickoff failed', error);
    }
  }, []);

  const sendLessonOverview = useCallback((context: SlideContextPayload | null) => {
    if (!context?.progress || !context.lessonTitle) {
      if (!context?.progress) {
        lastLessonOverviewKeyRef.current = null;
      }
      return;
    }
    if (context.slide) {
      return;
    }
    const total = context.progress.total ?? 0;
    if (!total) return;
    const completed = context.progress.completed ?? 0;
    const remainingTitles = context.progress.pendingTitles ?? [];
    const key = `${context.lessonTitle}:${completed}/${total}`;
    if (lastLessonOverviewKeyRef.current === key) {
      return;
    }
    const targetClient = clientRef.current;
    if (!targetClient || !backendReadyRef.current) return;

    const remaining = Math.max(total - completed, 0);
    const nextUp = remainingTitles.length ? remainingTitles[0] : undefined;
    let prompt = `Back at "${context.lessonTitle}" — ${completed}/${total} slides done.`;
    if (remaining > 0 && nextUp) {
      prompt += ` Next up is "${nextUp}". Continue there or revisit something else?`;
    } else if (remaining > 0) {
      prompt += ` ${remaining} slides remain. Want to pick the next one or review an earlier slide?`;
    } else {
      prompt += ` Everything's wrapped. Fancy a recap or a stretch activity?`;
    }

    try {
      targetClient.send([{ text: prompt }], true);
      lastLessonOverviewKeyRef.current = key;
      devLogger.log('📚 Sent lesson overview prompt', { prompt, lessonTitle: context.lessonTitle });
    } catch (error) {
      devLogger.warn('sendLessonOverview failed', error);
    }
  }, []);

  useEffect(() => { clientRef.current = client; }, [client]);

  // timer
  useEffect(() => {
    let t: NodeJS.Timeout | null = null;
    if (isAgentActive && sessionStartRef.current) {
      t = setInterval(() => {
        const elapsed = Date.now() - sessionStartRef.current!;
        const mm = Math.floor(elapsed / 60000).toString().padStart(2, '0');
        const ss = Math.floor((elapsed % 60000) / 1000).toString().padStart(2, '0');
        setSessionDuration(`${mm}:${ss}`);
      }, 1000);
    }
    return () => { if (t) clearInterval(t); };
  }, [isAgentActive]);

  // Cost optimization: Sync journalId ref when prop changes
  useEffect(() => {
    currentJournalIdRef.current = journalId ?? null;
    // Reset pre-flight if journal changes (need to re-auth for new context)
    if (journalId && preFlightData?.journalContext?.id !== journalId) {
      setIsPreFlightReady(false);
    }
  }, [journalId, preFlightData?.journalContext?.id]);

  // Cost optimization: Pre-flight function for lazy connection
  const performPreFlight = useCallback(async (targetJournalId: string) => {
    try {
      devLogger.log('🛫 Starting pre-flight for journal:', targetJournalId);

      // 1. Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token ?? null;

      if (!token) {
        devLogger.warn('Pre-flight: No auth token available');
        setIsPreFlightReady(false);
        return;
      }

      // 2. Check credits
      const { data: userCredits } = await supabase
        .from('user_credits')
        .select('credits_left')
        .eq('user_id', user?.id)
        .single();

      const credits = userCredits?.credits_left ?? 0;

      if (credits <= 0) {
        // Store that we're out of credits - will show dialog on talk attempt
        setPreFlightData({ authToken: token, userCredits: 0, userProfile: null, journalContext: null });
        setIsPreFlightReady(true); // Ready, but will fail gracefully on connect
        devLogger.log('🛫 Pre-flight complete (no credits)');
        return;
      }

      // 3. Fetch user profile + memories in parallel
      const [profileResult, journalResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('full_name, age, goals, interaction_preferences, memories')
          .eq('id', user?.id)
          .single(),
        supabase
          .from('journals')
          .select('id, name, summary, todo_items, tutor_instructions')
          .eq('id', targetJournalId)
          .single(),
      ]);

      setPreFlightData({
        authToken: token,
        userCredits: credits,
        userProfile: profileResult.data,
        journalContext: journalResult.data,
      });
      setIsPreFlightReady(true);
      currentJournalIdRef.current = targetJournalId;

      // 4. Pre-load AudioWorklet in background (non-blocking)
      GeminiAudioManager.preloadAudioWorklet().catch(() => {
        // Ignore - will load on demand when user clicks Talk
      });

      devLogger.log('🛫 Pre-flight complete, ready for connection:', {
        hasToken: !!token,
        credits,
        journalId: targetJournalId,
        hasResumptionToken: !!getResumptionToken(targetJournalId),
      });

    } catch (error) {
      devLogger.warn('Pre-flight failed:', error);
      setIsPreFlightReady(false);
    }
  }, [user?.id]);

  // Infinite sessions: Auto-reconnect function for session extension
  const handleAutoReconnect = useCallback(async () => {
    const handle = latestResumptionHandleRef.current;
    if (!handle) {
      devLogger.warn('Auto-reconnect failed: no resumption handle available');
      setIsExtendingSession(false);
      return;
    }

    if (isAutoReconnectingRef.current) {
      devLogger.log('Auto-reconnect already in progress');
      return;
    }

    isAutoReconnectingRef.current = true;
    devLogger.log('🔄 Auto-reconnecting to extend session...');

    try {
      // Start buffering audio during reconnection
      isBufferingRef.current = true;
      audioBufferRef.current = [];

      // Get current client reference
      const currentClient = clientRef.current;
      const currentAudioMgr = audioManager;

      // Disconnect current session gracefully
      if (currentClient) {
        currentClient.disconnect();
      }

      // Brief pause for cleanup
      await new Promise(r => setTimeout(r, 200));

      // Store the handle in session storage for the reconnect
      const journalIdForReconnect = currentJournalIdRef.current;
      if (journalIdForReconnect) {
        storeResumptionToken(journalIdForReconnect, handle);
      }

      // Reconnect by calling handleConnectAgent (it will pick up the resumption token)
      devLogger.log('🔄 Triggering reconnection with resumption handle...');

      // Reset status to allow reconnection
      setAgentStatus('disconnected');

      // Small delay then trigger reconnection
      setTimeout(() => {
        // Use the ref to call handleConnectAgent (avoids circular dependency)
        if (handleConnectAgentRef.current) {
          handleConnectAgentRef.current();
        }
      }, 100);

    } catch (error) {
      devLogger.warn('Auto-reconnect error:', error);
      setIsExtendingSession(false);
    } finally {
      isAutoReconnectingRef.current = false;
    }
  }, [audioManager]);

  const handleConnectAgent = useCallback(async (mode?: string) => {
    if (agentStatus === 'connecting' || agentStatus === 'connected') return;

    // Cost optimization: Use pre-flight credits if available, otherwise fetch
    let credits = preFlightData?.userCredits ?? null;
    if (credits === null) {
      const { data: userCredits } = await supabase
        .from('user_credits')
        .select('credits_left')
        .eq('user_id', user?.id)
        .single();
      credits = userCredits?.credits_left ?? 0;
    }

    if (!credits || credits <= 0) {
      console.log('❌ No credits available for voice connection');
      try {
        window.dispatchEvent(new CustomEvent('open_out_of_credits_dialog'));
      } catch (error) {
        console.error('Failed to show out of credits dialog:', error);
      }
      return;
    }

    // Cost optimization: Check for session resumption token
    const journalIdForResumption = currentJournalIdRef.current;
    const resumptionToken = journalIdForResumption ? getResumptionToken(journalIdForResumption) : null;
    if (resumptionToken) {
      devLogger.log('🔄 Will attempt session resumption for journal:', journalIdForResumption);
    }

    try {
      setAgentStatus('connecting');
      Sentry.addBreadcrumb({ category: 'live', level: 'info', message: 'connect.start' });
      const audioMgr = new GeminiAudioManager(null);
      setAudioManager(audioMgr);

      const liveClient = new MultimodalLiveClient({ userId: user?.id });
      // wire events
      liveClient
        .on('open', async () => {
          try { Sentry.addBreadcrumb({ category: 'live', level: 'info', message: 'ws.open' }); } catch { }
          // Ensure playback context is ready so streamed audio can play
          try { await audioMgr.ensurePlaybackContext(); } catch { }
          // Authenticate and send metadata ASAP after open
          try {
            // Cost optimization: Use pre-flight auth token if available
            let token = preFlightData?.authToken ?? null;
            if (!token) {
              const { data: { session } } = await supabase.auth.getSession();
              token = session?.access_token ?? null;
            }
            if (token) {
              try { Sentry.addBreadcrumb({ category: 'live', level: 'info', message: 'auth.sent' }); } catch { }
              liveClient.authenticate(token);
            }
            liveClient.sendMetadata({
              sessionId: activeSessionId || undefined,
              topicId: topicId || undefined,
              documentId: selectedDocument || undefined,
              mode: mode || undefined,
              resumptionToken: resumptionToken || undefined,
              // Only skip greeting on auto-extension, not manual reconnect
              isExtension: isAutoReconnectingRef.current || undefined,
            });
            try { Sentry.addBreadcrumb({ category: 'live', level: 'info', message: 'metadata.sent' }); } catch { }
          } catch (e) {
            devLogger.warn('Live auth/metadata send failed', e);
            Sentry.captureException(e as any, { tags: { phase: 'auth_metadata' } });
          }
          // Start mic capture and route frames to Live API when unmuted
          try {
            await audioMgr.startAudioCapture();
            const toB64 = (buf: ArrayBuffer) => {
              const bytes = new Uint8Array(buf);
              let binary = '';
              const step = 0x8000;
              for (let i = 0; i < bytes.length; i += step) {
                binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + step)) as unknown as number[]);
              }
              return btoa(binary);
            };
            // Cost optimization: Buffer or forward frames based on connection state
            audioMgr.setFrameSink((buf: ArrayBuffer) => {
              try {
                const ws = (liveClient as any).ws as WebSocket | null;
                // If not connected yet, buffer the audio
                if (!ws || ws.readyState !== WebSocket.OPEN || !backendReadyRef.current) {
                  if (isBufferingRef.current && audioBufferRef.current.length < MAX_BUFFER_FRAMES) {
                    audioBufferRef.current.push(buf);
                  }
                  return;
                }
                const b64 = toB64(buf);
                liveClient.sendRealtimeInput([{ mimeType: 'audio/pcm;rate=16000', data: b64 } as any]);
              } catch { }
            });
            // Start muted by default (push-to-talk UX); still sends silence to keep session smooth
            try { await audioMgr.mute(); } catch { }
          } catch (e) {
            devLogger.warn('Mic capture initialization failed', e);
            Sentry.captureException(e as any, { tags: { phase: 'mic_capture' } });
          }
          // Defer marking as connected until backend is ready
        })
        .on('status', (msg: string) => {
          if (msg === 'auth_ok') {
            try { Sentry.addBreadcrumb({ category: 'live', level: 'info', message: 'auth.ok' }); } catch { }
            setAgentStatus('authenticating');
          }
          if (msg === 'auth_failed') {
            try { Sentry.captureMessage('live.auth_failed', { level: 'error' }); } catch { }
            setAgentStatus('error');
          }
          if (msg === 'payload_too_large') {
            devLogger.warn('⚠️ Dropped oversized vision frame by server');
          }
          if (typeof msg === 'string' && msg.toLowerCase().includes('backend is ready')) {
            backendReadyRef.current = true;
            setAgentStatus('connected');
            setIsAgentActive(true);
            hasEverConnectedRef.current = true;
            sessionStartRef.current = Date.now();
            onAgentActivityChange?.(true);
          }
          if (msg === 'out_of_credits') {
            // Stop gracefully; then show out-of-credits dialog
            try { liveClient.disconnect(); } catch { }
            try { audioMgr.setFrameSink(null); } catch { }
            setAgentStatus('error');
            setIsAgentActive(false);
            sessionStartRef.current = null;
            onAgentActivityChange?.(false);
            try { Sentry.captureMessage('live.out_of_credits', { level: 'warning' }); } catch { }
            try { window.dispatchEvent(new CustomEvent('open_out_of_credits_dialog', { detail: { source: hasEverConnectedRef.current ? 'session' : 'preflight' } })); } catch { }
          }
        })
        .on('close', (ev) => {
          try {
            const ce = ev as CloseEvent;
            Sentry.addBreadcrumb({
              category: 'live',
              level: 'info',
              message: `ws.close code=${ce?.code ?? 'n/a'} reason=${ce?.reason ?? ''}`,
            });
            if (ce && ce.code && ce.code !== 1000) {
              Sentry.captureMessage(`live.disconnect code=${ce.code}`, { level: 'warning' });
            }
          } catch { }
          try { audioMgr.setFrameSink(null); } catch { }
          setAgentStatus('disconnected');
          setIsAgentActive(false);
          sessionStartRef.current = null;
          onAgentActivityChange?.(false);
          backendReadyRef.current = false;
          lastSlideContextKeyRef.current = null;
          lastSlideNudgeKeyRef.current = null;
          lastLessonOverviewKeyRef.current = null;
        })
        .on('setupcomplete', () => {
          backendReadyRef.current = true;
          setAgentStatus('connected');
          setIsAgentActive(true);
          hasEverConnectedRef.current = true;
          sessionStartRef.current = Date.now();
          onAgentActivityChange?.(true);

          // Cost optimization: Flush buffered audio frames
          if (isBufferingRef.current && audioBufferRef.current.length > 0) {
            devLogger.log(`🔊 Flushing ${audioBufferRef.current.length} buffered audio frames`);
            const toB64 = (buf: ArrayBuffer) => {
              const bytes = new Uint8Array(buf);
              let binary = '';
              const step = 0x8000;
              for (let i = 0; i < bytes.length; i += step) {
                binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + step)) as unknown as number[]);
              }
              return btoa(binary);
            };
            for (const buf of audioBufferRef.current) {
              try {
                liveClient.sendRealtimeInput([{ mimeType: 'audio/pcm;rate=16000', data: toB64(buf) } as any]);
              } catch { }
            }
            audioBufferRef.current = [];
          }
          isBufferingRef.current = false;

          // Infinite sessions: Clear extending indicator on successful reconnect
          setIsExtendingSession(false);

          const context = slideContextRef.current;
          if (context) {
            sendSlideContext(context, true);
            if (context.slide) {
              sendSlideKickoff(context);
            } else {
              sendLessonOverview(context);
            }
          }
        })
        .on('audio', (buf) => { audioMgr.playAudio(buf); })
        .on('inputTranscription', (t) => {
          const incoming = (t.inputTranscription.text || '').trim();
          if (!incoming || !setMessages) return;
          const confidence = t.inputTranscription.confidence;
          const ensureId = () => {
            if (!userStreamingMsgIdRef.current) {
              userStreamingMsgIdRef.current = `${Date.now()}_${messageSeqRef?.current ?? 0}_user`;
            }
            return userStreamingMsgIdRef.current!;
          };
          const prev = userStreamingTextRef.current || '';
          let nextText: string;
          if (incoming.startsWith(prev)) nextText = incoming;
          else if (prev && prev.startsWith(incoming)) nextText = prev;
          else if (incoming.includes(prev)) nextText = incoming;
          else if (prev && prev.includes(incoming)) nextText = prev;
          else nextText = (prev ? prev + ' ' : '') + incoming;
          userStreamingTextRef.current = nextText;
          const id = ensureId();
          setMessages((prevMsgs: any[]) => {
            const idx = prevMsgs.findIndex(m => m.id === id);
            if (idx === -1) {
              return [
                ...prevMsgs,
                { id, type: 'user', content: nextText, timestamp: new Date(), streaming: true, source: 'voice', confidence },
              ];
            }
            const copy = [...prevMsgs];
            copy[idx] = { ...copy[idx], content: nextText, confidence };
            return copy;
          });
        })
        .on('outputTranscription', (t) => {
          const incoming = (t.outputTranscription.text || '').trim();
          if (!incoming) return;
          setIsAgentTyping(true);

          if (setMessages) {
            const ensureMessageId = () => {
              if (!streamingMsgIdRef.current) {
                streamingMsgIdRef.current = `${Date.now()}_${messageSeqRef?.current ?? 0}_agent`;
              }
              return streamingMsgIdRef.current!;
            };

            // Merge incoming chunk with previously seen text to avoid truncation
            const prev = streamingTextRef.current || '';
            let nextText: string;
            if (incoming.startsWith(prev)) {
              nextText = incoming;
            } else if (prev && prev.startsWith(incoming)) {
              // incoming is shorter (rare) – keep prev to avoid flicker
              nextText = prev;
            } else if (incoming.includes(prev)) {
              nextText = incoming;
            } else if (prev && prev.includes(incoming)) {
              nextText = prev;
            } else {
              nextText = (prev ? prev + ' ' : '') + incoming;
            }
            streamingTextRef.current = nextText;

            const id = ensureMessageId();
            setMessages((prevMsgs: any[]) => {
              const idx = prevMsgs.findIndex(m => m.id === id);
              if (idx === -1) {
                return [
                  ...prevMsgs,
                  { id, type: 'agent', content: nextText, timestamp: new Date(), streaming: true },
                ];
              }
              const copy = [...prevMsgs];
              copy[idx] = { ...copy[idx], content: nextText };
              return copy;
            });
          } else if (appendMessage) {
            // Fallback path if no setMessages provided
            appendMessage({
              id: `${Date.now()}_${messageSeqRef?.current ?? 0}_agent`,
              type: 'agent' as const,
              content: incoming,
              timestamp: new Date(),
            });
          }
        })
        .on('interrupted', () => {
          // Stop any current playback and clear streaming state to avoid stale typing
          try { audioMgr.stopCurrentAudio(); } catch { }
          setIsAgentTyping(false);
          const agentId = streamingMsgIdRef.current;
          const userId = userStreamingMsgIdRef.current;
          if (setMessages && (agentId || userId)) {
            try {
              setMessages((prev: any[]) => prev.map(m =>
                m.id === agentId || m.id === userId ? { ...m, streaming: false } : m
              ));
            } catch { }
          }
          // Reset streaming refs
          streamingMsgIdRef.current = null;
          streamingTextRef.current = '';
          userStreamingMsgIdRef.current = null;
          userStreamingTextRef.current = '';
          devLogger.log('⏹️ Interrupted: cleared streaming and halted playback');
        })
        .on('turncomplete', () => {
          setIsAgentTyping(false);
          if (setMessages) {
            const agentId = streamingMsgIdRef.current;
            const userId = userStreamingMsgIdRef.current;
            setMessages((prev: any[]) => prev.map(m =>
              m.id === agentId || m.id === userId ? { ...m, streaming: false } : m
            ));
            // Increment unsaved count for each finalized message
            if (setUnsavedMessageCount) {
              let inc = 0;
              if (agentId) inc += 1;
              if (userId) inc += 1;
              if (inc > 0) setUnsavedMessageCount((c) => c + inc);
            }
          }
          // reset streaming refs
          streamingMsgIdRef.current = null;
          streamingTextRef.current = '';
          userStreamingMsgIdRef.current = null;
          userStreamingTextRef.current = '';

          // Infinite sessions: Check if we need to auto-reconnect after GoAway
          if (pendingReconnectRef.current && latestResumptionHandleRef.current) {
            devLogger.log('🔄 Turn complete - triggering auto-reconnect for session extension');
            pendingReconnectRef.current = false;
            // Trigger auto-reconnect (will be handled by the function we'll create)
            handleAutoReconnect();
          }
        })
        .on('blockAdded', (data) => {
          devLogger.log('📝 Block added by AI agent:', data);
          onBlockAdded?.(data);
        })
        .on('blockUpdated', (data) => {
          devLogger.log('📝 Block updated by AI agent:', data);
          onBlockUpdated?.(data);
        })
        .on('journalUpdated', (data) => {
          devLogger.log('📋 Journal updated by AI agent (todos):', data);
          onJournalUpdated?.(data);
        })
        // Cost optimization + Infinite sessions: Store session resumption tokens
        .on('resumptionToken', (token: string) => {
          // Store in ref for auto-reconnect
          latestResumptionHandleRef.current = token;
          // Also store per-journal for cross-session resumption
          const journalIdForToken = currentJournalIdRef.current;
          if (journalIdForToken && token) {
            storeResumptionToken(journalIdForToken, token);
            devLogger.log('💾 Stored resumption token for journal:', journalIdForToken);
          }
        })
        // Infinite sessions: Handle GoAway notification (60s before termination)
        .on('goaway', (timeLeft: number) => {
          devLogger.log(`⏱️ GoAway received: ${timeLeft}s remaining - will auto-reconnect`);
          // Mark that we should reconnect after current turn completes
          pendingReconnectRef.current = true;
          // Show extending indicator in UI
          setIsExtendingSession(true);
          // Fallback: auto-clear indicator after 5 seconds if setupcomplete doesn't fire
          setTimeout(() => {
            setIsExtendingSession(false);
          }, 5000);
        });

      setClient(liveClient);
      await liveClient.connect();
    } catch (e) {
      console.error('Live connect error', e);
      setAgentStatus('error');
      setIsAgentActive(false);
      onAgentActivityChange?.(false);
    }
  }, [
    agentStatus,
    appendMessage,
    messageSeqRef,
    onAgentActivityChange,
    sendSlideContext,
    sendSlideKickoff,
    sendLessonOverview,
    setMessages,
    user?.id,
  ]);

  // Keep ref in sync for auto-reconnect to use
  useEffect(() => { handleConnectAgentRef.current = handleConnectAgent; }, [handleConnectAgent]);

  const handleStopConversation = useCallback(() => {
    try { audioManager?.stopCurrentAudio(); } catch { }
    try { audioManager?.setFrameSink(null); } catch { }
    try { client?.disconnect(); } catch { }
    try { audioManager?.stop(); } catch { }
    setClient(null);
    setAudioManager(null);
    setAgentStatus('disconnected');
    setIsAgentActive(false);
    setIsAgentTyping(false);
    sessionStartRef.current = null;
    onAgentActivityChange?.(false);
    backendReadyRef.current = false;
    lastSlideContextKeyRef.current = null;
    lastSlideNudgeKeyRef.current = null;
    lastLessonOverviewKeyRef.current = null;
  }, [audioManager, client, onAgentActivityChange]);

  const sendText = useCallback((text: string) => {
    if (!client) return;
    client.send({ text }, true);
  }, [client]);

  const sendContextUpdate = useCallback((payload: object) => {
    if (!client) return;
    client.sendContextUpdate(payload);
  }, [client]);

  // cleanup on unmount
  useEffect(() => () => {
    try { client?.disconnect(); } catch { }
  }, [client]);

  // Listen to VAD events from audio worklet to drive frame sending
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { speaking?: boolean };
      const speaking = !!detail?.speaking;
      setIsUserSpeakingVad(speaking);
    };
    window.addEventListener('sb-vad-speaking', handler as EventListener);
    return () => window.removeEventListener('sb-vad-speaking', handler as EventListener);
  }, []);

  const sendYouTubeContext = useCallback(async () => {
    if (!client || !getYouTubePlayer || !currentYouTubeVideoId) return;
    try {
      const player = getYouTubePlayer();
      if (!player) return;
      const currentTime = await player.getCurrentTime();
      const ctx = await createYouTubeContext(currentYouTubeVideoId, currentTime);
      if (!ctx) return;
      // Debounce by 30s buckets
      const bucket = `${ctx.videoId}_${Math.floor(ctx.currentTime / 30)}`;
      const now = Date.now();
      if (lastYTKeyRef.current === bucket && now - lastYTTimeRef.current < 10000) {
        return;
      }
      lastYTKeyRef.current = bucket;
      lastYTTimeRef.current = now;
      // Send thumbnail if available
      const resp = await fetch(ctx.thumbnail);
      if (resp.ok) {
        const blob = await resp.blob();
        const reader = new FileReader();
        const b64 = await new Promise<string>((res) => { reader.onload = () => res((reader.result as string).split(',')[1]); reader.readAsDataURL(blob); });
        client.sendRealtimeInput([{ mimeType: 'image/jpeg', data: b64 } as any]);
      }
      const msg = formatYouTubeContextMessage(ctx);
      client.send({ text: msg }, true);
    } catch (e) {
      devLogger.warn('sendYouTubeContext failed', e);
    }
  }, [client, getYouTubePlayer, currentYouTubeVideoId]);

  const sendFrame = useCallback(async () => {
    if (!client) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    const now = Date.now();
    if (isCaptureInProgressRef.current) return;
    if (now - lastFrameSentRef.current < 2000) return; // throttle 2s
    isCaptureInProgressRef.current = true;
    try {
      if (currentYouTubeVideoId && getYouTubePlayer) {
        devLogger.log('📺 Sending YouTube context/frame');
        await sendYouTubeContext();
        lastFrameSentRef.current = now;
        return;
      }
      let b64: string | null = null;
      if (screenStream && screenStream.getVideoTracks().length > 0) {
        devLogger.log('📸 Capturing screen share frame...');
        b64 = await captureScreenShare(screenStream);
      } else if (getCanvasEl) {
        const el = getCanvasEl();
        if (el) {
          devLogger.log('🖼️ Capturing canvas viewport frame...');
          b64 = await captureCanvasViewport(el);
        } else {
          devLogger.warn('⚠️ getCanvasEl returned null');
        }
      }
      if (b64) {
        client.sendRealtimeInput([{ mimeType: 'image/jpeg', data: b64 } as any]);
        lastFrameSentRef.current = now;
        devLogger.log('📤 Vision frame sent (jpeg), chars=', b64.length);
      } else {
        devLogger.warn('⚠️ No frame available to send');
      }
    } catch (e) {
      devLogger.warn('sendFrame failed', e);
    } finally {
      isCaptureInProgressRef.current = false;
    }
  }, [client, getCanvasEl, screenStream, getYouTubePlayer, currentYouTubeVideoId, sendYouTubeContext]);

  // Rising edge: send immediately when speaking starts
  useEffect(() => {
    if (agentStatus !== 'connected') return;
    if (isUserSpeakingVad && !prevSpeakingRef.current) {
      sendFrame();
    }
    prevSpeakingRef.current = isUserSpeakingVad;
  }, [isUserSpeakingVad, agentStatus, sendFrame]);

  // While speaking: heartbeat every 2s
  useEffect(() => {
    if (agentStatus !== 'connected' || !isUserSpeakingVad) return;
    const t = setInterval(() => { sendFrame(); }, 2000);
    return () => clearInterval(t);
  }, [isUserSpeakingVad, agentStatus, sendFrame]);

  // Initial frame on connect
  useEffect(() => {
    if (agentStatus === 'connected') {
      sendFrame();
    }
  }, [agentStatus, sendFrame]);

  // Slide context updates
  useEffect(() => {
    slideContextRef.current = slideContext ?? null;
    if (agentStatus !== 'connected' || !backendReadyRef.current) {
      return;
    }
    sendSlideContext(slideContext ?? null);
    if (slideContext?.slide) {
      sendSlideKickoff(slideContext);
    } else {
      sendLessonOverview(slideContext ?? null);
    }
  }, [agentStatus, slideContext, sendLessonOverview, sendSlideContext, sendSlideKickoff]);

  // Push-to-talk handlers (defined before keyboard listeners to avoid TDZ)
  const handlePushToTalkStart = useCallback(() => {
    // Cost optimization: If not connected, start buffering and trigger connection
    if (agentStatus === 'disconnected' || agentStatus === 'error') {
      devLogger.log('🎤 Push-to-talk triggered, starting buffering and connection');
      isBufferingRef.current = true;
      audioBufferRef.current = [];
      // Connection will be triggered by the calling component 
      // (typically wrappedConnect in useLiveAdapter)
    }

    setIsUserMuted(false);
    try { audioManager?.unmute(); } catch { }
    // Kick off a frame immediately when user starts talking
    try { sendFrame(); } catch { }
  }, [sendFrame, audioManager, agentStatus]);

  const handlePushToTalkEnd = useCallback(() => {
    if (!isPushToTalkLocked) {
      setIsUserMuted(true);
      try { audioManager?.mute(); } catch { }
    }
  }, [isPushToTalkLocked, audioManager]);

  // Keyboard push-to-talk (spacebar)
  const keyHoldRef = useRef(false);
  const isTextInput = (el: EventTarget | null) => {
    const node = el as HTMLElement | null;
    if (!node) return false;
    const tag = (node.tagName || '').toLowerCase();
    const editable = (node as HTMLElement).isContentEditable;
    return tag === 'input' || tag === 'textarea' || editable;
  };
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.code !== 'Space') return;
    if (isTextInput(event.target)) return; // don’t steal typing
    if (!(agentStatus === 'connected' && !isPushToTalkLocked)) return;
    if (keyHoldRef.current) return; // already active
    keyHoldRef.current = true;
    event.preventDefault();
    try { handlePushToTalkStart(); } catch { }
  }, [agentStatus, isPushToTalkLocked, handlePushToTalkStart]);
  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    if (event.code !== 'Space') return;
    if (!keyHoldRef.current) return;
    keyHoldRef.current = false;
    event.preventDefault();
    try { handlePushToTalkEnd(); } catch { }
  }, [handlePushToTalkEnd]);

  useEffect(() => {
    if (!(agentStatus === 'connected')) return;
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [agentStatus, handleKeyDown, handleKeyUp]);

  // YouTube auto-unlock push-to-talk (align with legacy)
  useEffect(() => {
    const onYTPlaying = () => {
      if (isPushToTalkLocked && isAgentActive) {
        setIsPushToTalkLocked(false);
        if (!isUserMuted) {
          setIsUserMuted(true);
          try { audioManager?.mute(); } catch { }
        }
      }
    };
    window.addEventListener('youtube-playing', onYTPlaying);
    return () => window.removeEventListener('youtube-playing', onYTPlaying);
  }, [isPushToTalkLocked, isAgentActive, isUserMuted, audioManager]);

  // Suspend frame sending (and soft-mute) when tab hidden; restore on visible
  const wasUnmutedRef = useRef(false);

  // Phase 8a: Build and send transcript sync to server
  const sendTranscriptSync = useCallback(() => {
    const targetClient = clientRef.current;
    if (!targetClient || !backendReadyRef.current || !getMessages) return;

    const messages = getMessages();
    if (!messages || messages.length === 0) return;

    // Format messages as transcript for server
    const transcript = messages
      .filter((m: any) => m.content && (m.type === 'user' || m.type === 'agent'))
      .map((m: any) => ({
        role: m.type === 'user' ? 'user' : 'agent',
        content: m.content
      }));

    if (transcript.length >= 2) {
      try {
        targetClient.sendTranscriptSync(transcript);
        devLogger.log('📝 Sent transcript sync to server', { messageCount: transcript.length });
      } catch (e) {
        devLogger.warn('sendTranscriptSync failed', e);
      }
    }
  }, [getMessages]);

  useEffect(() => {
    const onVis = () => {
      const hidden = document.hidden;
      if (hidden) {
        wasUnmutedRef.current = !isUserMuted;
        try { audioManager?.mute(); } catch { }
        // Phase 8a: Send transcript sync when tab becomes hidden (user switching away)
        sendTranscriptSync();
      } else {
        if (wasUnmutedRef.current) {
          try { audioManager?.unmute(); } catch { }
        }
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [audioManager, isUserMuted, sendTranscriptSync]);

  // Phase 8a: Send transcript sync every 30 seconds during active session
  useEffect(() => {
    if (agentStatus !== 'connected') return;

    // Send initial sync after short delay
    const initialTimeout = setTimeout(() => {
      sendTranscriptSync();
    }, 5000);

    // Then every 30 seconds
    const interval = setInterval(() => {
      sendTranscriptSync();
    }, 30000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [agentStatus, sendTranscriptSync]);

  // Mic controls
  const toggleMute = useCallback(async () => {
    if (!audioManager) { setIsUserMuted(m => !m); return; }
    setIsUserMuted((prev) => {
      const next = !prev;
      // Soft-mute keeps frames flowing (silence)
      if (next) {
        audioManager.mute().catch(() => { });
      } else {
        audioManager.unmute().catch(() => { });
      }
      return next;
    });
  }, [audioManager]);



  const handleToggleLock = useCallback(() => {
    setIsPushToTalkLocked((prevLocked) => {
      const newLocked = !prevLocked;
      // When locking: unmute for audio passthrough
      // When unlocking: mute to return to push-to-talk mode
      const newMuted = !newLocked;
      setIsUserMuted(newMuted);

      // Update audio manager state
      if (audioManager) {
        if (newMuted) {
          audioManager.mute().catch(() => { });
        } else {
          audioManager.unmute().catch(() => { });
        }
      }

      return newLocked;
    });
  }, [audioManager]);

  return {
    agentStatus,
    isAgentActive,
    isUserMuted,
    isPushToTalkLocked,
    sessionDuration,
    isAgentTyping,
    isUserSpeakingVad,
    handleConnectAgent,
    handleStopConversation,
    toggleMute,
    handlePushToTalkStart,
    handlePushToTalkEnd,
    handleToggleLock,
    sendText,
    sendContextUpdate,
    audioManager,
    // Cost optimization: Pre-flight functions
    performPreFlight,
    isPreFlightReady,
    // Infinite sessions: Session extension indicator
    isExtendingSession,
  };
}
